import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logoSrc from '../../assets/innovactive-logo.png';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  RocketLaunchIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../Notifications/NotificationBell';
import { useNavigationSettings } from '../../hooks/useNavigationSettings';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: navigationSettings } = useNavigationSettings();

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  // Navigation publique (filtrée selon les paramètres)
  const navigation = [
    { name: 'Projets', href: '/projects', key: 'projects' },
    { name: 'Appels à projet', href: '/call-for-projects', key: 'callForProjects' },
    { name: 'Financements', href: '/financing', key: 'financing' },
  ].filter(item => navigationSettings?.[item.key] !== false);

  // Navigation utilisateur (filtrée selon les paramètres)
  const userNavigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: RocketLaunchIcon, key: 'dashboard' },
    { name: 'Mes projets', href: '/my-projects', icon: RocketLaunchIcon, key: 'myProjects' },
    { name: 'Mes Candidatures', href: '/my-applications', icon: DocumentTextIcon, key: 'myApplications' },
    { name: 'Mes favoris', href: '/favorites', icon: HeartIcon, key: 'favorites' },
    { name: 'Messagerie', href: '/messaging', icon: ChatBubbleLeftRightIcon, key: 'messaging' },
    { name: 'Matching', href: '/matching', icon: SparklesIcon, key: 'matching' },
    { name: 'Contacts', href: '/contacts', icon: UserCircleIcon, key: 'contacts' },
    { name: 'Profil', href: '/profile', icon: Cog6ToothIcon, key: 'profile' },
  ].filter(item => navigationSettings?.[item.key] !== false);

  // Ajouter les liens d'administration selon le rôle et les paramètres
  const adminNavigation = [];
  if (user?.role === 'admin' && navigationSettings?.admin !== false && navigationSettings?.showAdminMenu !== false) {
    adminNavigation.push({ name: 'Administration', href: '/admin', icon: Cog6ToothIcon });
  }
  if (user?.role === 'super-admin' && navigationSettings?.superAdmin !== false && navigationSettings?.showSuperAdminMenu !== false) {
    adminNavigation.push({ name: 'Super Admin', href: '/super-admin', icon: Cog6ToothIcon });
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex flex-col leading-tight" aria-label="Innovactive360° Bourse d'Idées — accueil">
              <img src={logoSrc} alt="Innovactive360°" width={1200} height={142} className="h-5 sm:h-6 w-auto" />
              <span className="text-xs font-medium uppercase tracking-wider text-gray-500 mt-0.5">
                Bourse d'Idées
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-gray-700 hover:text-primary-500 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <NotificationBell />
                {navigationSettings?.favorites !== false && (
                  <Link
                    to="/favorites"
                    className="p-2 text-gray-600 hover:text-primary-500 transition-colors"
                    title="Mes favoris"
                  >
                    <HeartIcon className="w-6 h-6" />
                  </Link>
                )}
                {navigationSettings?.messaging !== false && (
                  <Link
                    to="/messaging"
                    className="p-2 text-gray-600 hover:text-primary-500 transition-colors"
                    title="Messagerie"
                  >
                    <ChatBubbleLeftRightIcon className="w-6 h-6" />
                  </Link>
                )}
                {navigationSettings?.matching !== false && (
                  <Link
                    to="/matching"
                    className="p-2 text-gray-600 hover:text-primary-500 transition-colors"
                    title="Matching"
                  >
                    <SparklesIcon className="w-6 h-6" />
                  </Link>
                )}
                {navigationSettings?.showUserMenu !== false && (
                  <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                  {user?.avatar ? (
                    <img
                      className="h-8 w-8 rounded-full"
                      src={user.avatar}
                      alt={user.firstName}
                    />
                  ) : (
                    <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </span>
                    </div>
                  )}
                  <span className="text-gray-700 font-medium">
                    {user?.firstName}
                  </span>
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    {userNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <item.icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </Link>
                    ))}
                    {adminNavigation.length > 0 && (
                      <>
                        <div className="border-t border-gray-200 my-1"></div>
                        {adminNavigation.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <item.icon className="h-4 w-4 mr-3" />
                            {item.name}
                          </Link>
                        ))}
                      </>
                    )}
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                      Déconnexion
                    </button>
                  </div>
                )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary btn-sm"
                >
                  Inscription
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-gray-700 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 p-2"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 relative z-50">
            <div className="space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname === item.href
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-500 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Auth */}
            <div className="pt-4 border-t border-gray-200 mt-4">
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div className="px-3 py-2">
                    <div className="flex items-center space-x-3">
                      {user?.avatar ? (
                        <img
                          className="h-10 w-10 rounded-full"
                          src={user.avatar}
                          alt={user.firstName}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.role}
                        </p>
                      </div>
                    </div>
                  </div>
                  {userNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-500 hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  ))}
                  {adminNavigation.length > 0 && (
                    <>
                      <div className="border-t border-gray-200 my-2"></div>
                      {adminNavigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="flex items-center px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.name}
                        </Link>
                      ))}
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-500 hover:bg-gray-50"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-500 hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 rounded-md text-base font-medium bg-primary-500 text-white hover:bg-primary-600"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Inscription
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Overlay for mobile menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Overlay for user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
